import type {
  AppData,
  Credit,
  Customer,
  CustomerType,
  Sale,
  SaleItem,
  StockMovement,
} from "./types";

export const createId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const calculateDiscount = (
  amount: number,
  type: "none" | "percentage" | "flat" = "none",
  value = 0
) => {
  if (type === "percentage") return Math.min(amount, (amount * value) / 100);
  if (type === "flat") return Math.min(amount, value);
  return 0;
};

export const calculateLineTotal = (
  item: {
    quantity: number;
    unitPrice: number;
    discountType?: "none" | "percentage" | "flat";
    discountValue?: number;
  }
) => {
  const subtotal = item.quantity * item.unitPrice;
  return Math.max(
    0,
    subtotal -
      calculateDiscount(subtotal, item.discountType, item.discountValue)
  );
};

export const updateProductStock = (
  data: AppData,
  productId: string,
  quantityChange: number,
  type: StockMovement["type"],
  reason: string
) => {
  const product = data.products.find((item) => item.id === productId);
  if (!product) return;
  product.stockQuantity = Math.max(0, product.stockQuantity + quantityChange);
  data.stockMovements.unshift({
    id: createId("movement"),
    productId,
    type,
    quantityChange,
    resultingQuantity: product.stockQuantity,
    reason,
    createdAt: new Date().toISOString(),
  });
};

export const updateMaterialStock = (
  data: AppData,
  materialId: string,
  quantityChange: number
) => {
  const material = data.printMaterials.find((item) => item.id === materialId);
  if (!material) return;
  material.stockQuantity = Math.max(0, (material.stockQuantity ?? 0) + quantityChange);
};

export const makeSale = (
  items: SaleItem[],
  paymentMethod: string,
  totalAmount: number,
  invoiceNumber: string,
  discountAmount = 0,
  customerId?: string,
  paidAmount?: number,
  customerName?: string,
  customerType?: CustomerType
): Sale => ({
  id: createId("sale"),
  invoiceNumber,
  status: "completed",
  totalAmount,
  discountAmount,
  paymentMethod,
  customerId,
  customerName,
  customerType,
  paidAmount,
  createdAt: new Date().toISOString(),
  items,
});

export const makeCredit = (
  sale: Sale,
  items: SaleItem[],
  customer: Customer,
  advancePaid: number
): Credit => ({
  id: createId("credit"),
  saleId: sale.id,
  customerId: customer.id,
  customerName: customer.name,
  customerPhone: customer.phone,
  itemType: items.every((item) => item.itemType === "service") ? "service" : "product",
  itemName: items.length === 1 ? items[0].itemName : `Sale ${sale.invoiceNumber}`,
  advancePaid,
  amountDue: Math.max(0, sale.totalAmount - advancePaid),
  status: "outstanding",
  soldAt: sale.createdAt,
});

export const nextInvoiceNumber = (data: AppData): string => {
  const numbers = data.sales
    .map((sale) => parseInt(sale.invoiceNumber.replace(/[^\d]/g, ""), 10))
    .filter((n) => !Number.isNaN(n));
  return `#${(numbers.length > 0 ? Math.max(...numbers) : 1049) + 1}`;
};
